/**
 * Corrections of posted finance records: credit notes, invoice cancellation and payment void. Issued invoices and
 * recorded payments never change in place; each correction is a new, audited record with a reason.
 */
import type { Schema } from '@dealdesk/api-schema';
import { z } from 'zod';

import { M, zOptionalMoney, zRequiredString } from '@/business';
import { compare, isDecimalString } from '@/shared/lib/money';

export const reasonSchema = z.object({ reason: zRequiredString().pipe(z.string().max(500)) });
export type ReasonValues = z.infer<typeof reasonSchema>;

export const creditNoteSchema = z
  .object({
    reason: zRequiredString().pipe(z.string().max(500)),
    lines: z.array(z.object({ entryId: z.string(), room: z.string(), amount: zOptionalMoney() })),
  })
  .superRefine((v, ctx) => {
    const filled = v.lines.filter((l) => l.amount && isDecimalString(l.amount) && compare(l.amount, '0.00') > 0);
    if (!filled.length) ctx.addIssue({ code: 'custom', path: ['lines'], message: M.required });
    v.lines.forEach((l, i) => {
      if (l.amount && isDecimalString(l.amount) && compare(l.amount, l.room) > 0) ctx.addIssue({ code: 'custom', path: ['lines', i, 'amount'], message: 'form.amountMax', params: { max: l.room } });
    });
  });
export type CreditNoteValues = z.infer<typeof creditNoteSchema>;

/** Form defaults: one row per invoice line that can still be credited (positive, not fully credited). */
export function toCreditNoteValues(inv: Schema<'Invoice'>): CreditNoteValues {
  return { reason: '', lines: inv.lines.filter((l) => compare(l.amount, '0.00') > 0).map((l) => ({ entryId: l.id, room: l.amount, amount: null })) };
}

export function toCreditNoteRequest(v: CreditNoteValues): Schema<'CreditNoteCreate'> {
  return { reason: v.reason.trim(), lines: v.lines.flatMap((l) => (l.amount && compare(l.amount, '0.00') > 0 ? [{ entryId: l.entryId, amount: l.amount }] : [])) };
}
